<template>
    <div class="file-submission-corporate">
        <e-message v-if="outOfAttempts && !userHasAuthorizedAttempt" type="is-info">
            {{ translate('no_additional_attempts') }}
        </e-message>

        <e-tag v-if="outOfAttempts && userHasAuthorizedAttempt" type="is-warning">
            {{ translate('submitting_this_assignment_will_use_your_authorized_attempt') }}
        </e-tag>

        <e-message v-if="uploadError" type="is-danger">{{ uploadError }}</e-message>

        <display-rubric v-if="hasRubric" :rubric="{ uuid: hasRubric, namespace: rubricNamespace }" />

        <!-- Overview / Your Assignment (authored activity content). -->
        <display-content class="fs-instructions" :content="activity.activity_content.content" />

        <e-button v-if="canLaunchTurnitinFromActivity" type="is-primary" @click="showReviewDrawer = true">
            View Turnitin Assignment
        </e-button>

        <!-- Read-only (grader / preview): just the submitted files, no uploader. -->
        <attachments v-if="readOnly" :media="media" :deletable="false" />

        <!-- Unified "My Submissions" section: the list and the uploader live together
             (reference design). The list self-gates inside <submissions>; the uploader
             is revealed inline by the "Add another submission" affordance, or shown
             directly when there are no submissions yet. -->
        <section v-else class="fs-submissions">
            <div class="fs-submissions__head">
                <span class="fs-submissions__eyebrow">My Submissions</span>
                <span v-if="corporateCountLabel" class="fs-submissions__count">{{ corporateCountLabel }}</span>
            </div>

            <!-- Themed PrimeVue rule dividing the header from the submissions list. -->
            <Divider class="fs-submissions__divider" />

            <submissions
                :namespace="namespace"
                :show-header="false"
                inline-files
                @fetch-submissions="toggleSubmissionLoading"
            >
                <template #content="{ submission }">
                    <!-- Files render inline in the row (Submissions inline-files); the
                         slot only carries the Turnitin experience + the comment. -->
                    <div
                        v-if="
                            isTurnitinSubmission &&
                                (isTurnItInIntegrationEnabled || submission.submittable.media.length === 0)
                        "
                    >
                        <e-button expanded class="py-6">
                            <span
                                class="is-flex is-align-items-center is-justify-content-center is-flex-wrap-wrap"
                                :class="{
                                    'is-flex-direction-column-mobile': true,
                                    'is-flex-direction-row-tablet': true,
                                }"
                            >
                                <e-icon icon="check" class="is-success" />

                                <span class="ml-2 mt-2-mobile">File Uploaded in</span>

                                <span class="has-text-primary ml-1 mt-2-mobile">Turnitin</span>

                                <e-button
                                    class="button is-primary ml-2 mt-2-mobile"
                                    @click="showStudentTurnitinSubmission = true"
                                >
                                    View Assignment
                                </e-button>
                            </span>
                        </e-button>

                        <student-turnitin-assignment-uploader
                            v-if="isTurnitinSubmission"
                            multiple
                            :is-disabled="usedAllAttemptAuthorizations || readOnly || !hasPermissionToSubmit"
                            :read-only="readOnly"
                            :valid-files-updated="validFiles.length"
                            :max-file-size="maxFileSize"
                            :max-files="maxFiles"
                            :allowed-file-types="allowedFileTypes"
                            :lti-resource-link-id="activityContent.lti_resource_link_id"
                            :show-student-turnitin-submission="showStudentTurnitinSubmission"
                            from-student-submitted
                        />
                    </div>

                    <e-field v-if="submission.submittable.comments" label="Comments:">
                        <div>{{ submission.submittable.comments }}</div>
                    </e-field>
                </template>
            </submissions>

            <template v-if="canSubmit">
                <!-- Inline uploader: shown while adding, or directly when there are no
                     submissions yet (empty state). -->
                <div v-if="showUploadZone" class="fs-add">
                    <template v-if="isTurnitinSubmission">
                        <student-turnitin-assignment-uploader
                            multiple
                            :is-disabled="usedAllAttemptAuthorizations || readOnly || !hasPermissionToSubmit"
                            :read-only="readOnly"
                            :valid-files-updated="validFiles.length"
                            :max-file-size="maxFileSize"
                            :max-files="maxFiles"
                            :allowed-file-types="allowedFileTypes"
                            :lti-resource-link-id="activityContent.lti_resource_link_id"
                            :show-student-turnitin-submission="showStudentTurnitinSubmission"
                        />

                        <e-message type="is-info">
                            After uploading your assignment to Turnitin, it may take a few moments for your submission to
                            appear here.
                        </e-message>
                    </template>

                    <template v-else>
                        <!-- Upload dropzone (Figma 17111:44679): dashed box. -->
                        <div class="fs-upload">
                            <upload-input
                                ref="uploadInput"
                                multiple
                                :is-disabled="usedAllAttemptAuthorizations || readOnly || !hasPermissionToSubmit"
                                :max-file-size="maxFileSize"
                                :max-files="maxFiles"
                                :allowed-file-types="allowedFileTypes"
                                :show-size-limit="false"
                                @update:modelValue="updateFiles"
                                @validation-error="handleValidationError"
                            />
                        </div>

                        <div class="fs-add__meta">
                            <upload-constraints-hint
                                class="fs-add__hint"
                                :extensions="allowedExtensions"
                                :max-size-display="formatFileSize(maxFileSize)"
                            />

                            <PrimeVueButton
                                v-if="canCancelAdd"
                                label="Cancel"
                                text
                                size="small"
                                severity="secondary"
                                class="fs-add__cancel"
                                @click="cancelAdd"
                            />
                        </div>

                        <e-message v-if="showValidation" :type="minFilesNotMet ? 'is-warning' : 'is-success'">
                            {{ minFiles }} {{ translate('file', minFiles) }} {{ translate('required') }}.

                            {{ validFiles.length }} {{ translate('file', validFiles.length) }}
                            {{ translate('uploaded') }}.
                        </e-message>

                        <e-message
                            v-if="validationMessage"
                            aria-close-label="Close notification"
                            type="is-warning"
                            @close="validationMessage = ''"
                        >
                            <div class="has-text-left" v-html="validationMessage" />
                        </e-message>

                        <attachments
                            v-if="validFiles.length"
                            :media="validFiles"
                            inline
                            label="My Uploads"
                            class="submission-chip"
                            hide-download
                            @delete="deleteFile"
                        />
                    </template>

                    <div class="fs-comments">
                        <label class="fs-comments__label">Comments</label>
                        <e-input
                            v-model="comments"
                            :disabled="usedAllAttemptAuthorizations || readOnly || !hasPermissionToSubmit"
                            :rows="4"
                            type="textarea"
                            fluid
                        />
                    </div>

                    <late-notice v-if="isLate && !userHasAuthorizedAttempt" />

                    <attempt-authorization-notice
                        v-if="hasAnyAttemptAuthorizations"
                        :attempt-authorizations="userUnusedAuthorizedAttempts"
                    />

                    <e-field v-if="isUploading && uploadProgress !== null" label="Upload Progress">
                        <e-progress :value="uploadProgress" type="is-primary" size="is-medium" show-value>
                            {{ uploadProgress }}%
                        </e-progress>
                        <p class="help">Uploading files... Please don't close this page.</p>
                    </e-field>

                    <activity-completion-action-row v-show="!readOnly">
                        <e-button :disabled="disabled" :loading="isLoading" type="is-primary" @click="submit">
                            Submit &amp; Mark Complete
                        </e-button>
                    </activity-completion-action-row>
                </div>

                <!-- Quiet add affordance (when the learner already has submissions). -->
                <PrimeVueButton
                    v-else-if="canAddAnother"
                    label="Add another submission"
                    icon="pi pi-plus"
                    text
                    fluid
                    class="fs-add-affordance"
                    @click="startAdd"
                />
            </template>
        </section>

        <div v-show="false" ref="confirmationDialog">
            {{ translate('you_are_submitting_the_following_information') }}:
            <br />
            <br />
            <p v-if="isTurnitinSubmission">
                <e-icon icon="check" class="is-success" /> File Uploaded in
                <span class="has-text-primary ml-1">Turnitin</span>
            </p>
            <strong>{{ translate('attachments') }}:</strong>
            <br />
            <ul>
                <li v-for="(file, index) in files" :key="index">
                    {{ file.name }}
                </li>
            </ul>
            <br />
            <strong>Comments:</strong>
            <br />
            {{ comments }}
        </div>

        <turnitin-review-and-grade-drawer
            v-if="canLaunchTurnitinFromActivity"
            v-model:active="showReviewDrawer"
            :lti-resource-link-id="activityContent.lti_resource_link_id"
        />
    </div>
</template>

<script>
import PrimeVueButton from 'primevue/button';
import Divider from 'primevue/divider';
import { useI18n } from 'vue-i18n';

import FileSubmission from './FileSubmission.vue';

/**
 * Corporate presentation of the File Submission activity.
 *
 * Extends the stanbridge `FileSubmission.vue` so ALL behaviour (upload + validation,
 * S3/base64 processing, Turnitin, attempt authorization, submit, submissions) is
 * inherited unchanged; only the template + styles are overridden to match the
 * corporate learner view. Every learner/instructor gate is preserved. The
 * dispatcher selects this variant for the corporate client via
 * CORPORATE_RENDERER_OVERRIDES. Stanbridge FileSubmission.vue is untouched.
 *
 * Corporate UNIFIES the uploader and the submissions list into one "My Submissions"
 * section (reference design): existing attempts list first, then an inline uploader
 * revealed by an "Add another submission" affordance (or shown directly when there
 * are no submissions yet). `adding` is the only new local state; everything else
 * (files, validation, submit, attempts) is inherited.
 */
export default {
    name: 'FileSubmissionCorporate',

    components: { PrimeVueButton, Divider },

    extends: FileSubmission,

    // `extends` merges Options (data/computed/methods/mixins) but NOT the parent's
    // setup() return, so re-provide translate here.
    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    data: () => ({
        // Whether the inline uploader is expanded over an existing submissions list.
        adding: false,
    }),

    computed: {
        hasSubmissions() {
            return this.userSubmissions.length > 0;
        },

        // Watched so the uploader collapses back to the affordance after a new
        // submission lands (the parent's submit() doesn't know about `adding`).
        submissionCount() {
            return this.userSubmissions.length;
        },

        // "2 / 3" attempt count — only when multiple attempts are allowed. Suppressed
        // while submissions are still loading so it doesn't flash "0 / N" before the
        // real count arrives (the store is purged on navigation).
        corporateCountLabel() {
            if (!this.allowsMultipleSubmissions || this.submissionsLoading) {
                return null;
            }

            return `${this.userSubmissions.length} / ${this.maxAttempts}`;
        },

        // Show the inline uploader while adding, or directly when nothing is submitted yet.
        showUploadZone() {
            return this.adding || !this.hasSubmissions;
        },

        // The quiet "Add another submission" affordance: only with an existing list
        // and not already expanded (canSubmit, checked in the template, guarantees
        // attempts remain).
        canAddAnother() {
            return this.hasSubmissions && !this.adding;
        },

        // Cancel only makes sense when expanding over an existing list (the empty
        // state has nothing to collapse back to).
        canCancelAdd() {
            return this.adding && this.hasSubmissions;
        },
    },

    watch: {
        submissionCount(next, previous) {
            // A new submission landed → collapse the uploader back to the affordance.
            if (next > previous) {
                this.adding = false;
            }
        },
    },

    methods: {
        startAdd() {
            this.adding = true;
        },

        cancelAdd() {
            this.adding = false;
            this.reset();
        },
    },
};
</script>

<style scoped lang="scss">
.file-submission-corporate {
    // The corporate shell section owns the 860px content measure; fill it.
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .fs-instructions :deep(p) {
        color: #363636;
        font-size: 1rem; // 16px
        line-height: 1.5;
    }

    // Unified "My Submissions" section (list + inline uploader).
    .fs-submissions {
        display: flex;
        flex-direction: column;
    }

    .fs-submissions__head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 0.875rem;
        margin-bottom: 0;
    }

    // Header → list rule. Override PrimeVue's default 1.25rem block margins so it
    // sits tight under the header.
    .fs-submissions__divider.p-divider {
        margin: 0.75rem 0 0.25rem;
    }

    // Section header matches the redesign convention shared by the Podcast / Quiz /
    // Acknowledge cards (#363636, ~1rem, weight 600, sentence case) rather than the
    // pale uppercase-tracked eyebrow it started as, so it reads as part of the page.
    .fs-submissions__eyebrow {
        font-size: 1rem;
        font-weight: 600;
        color: #363636;
    }

    .fs-submissions__count {
        font-size: 0.85rem;
        color: #94a3b8;
    }

    // Inline uploader, revealed under the list. A hairline separates it from the
    // rows above so the section reads as one continuous list.
    .fs-add {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding-top: 1.125rem;
        border-top: 1px solid #f1f5f9;
    }

    // Upload dropzone: dashed box.
    .fs-upload {
        :deep(.upload-input-zone) {
            border: 1px dashed #cbd5e1;
            border-radius: 10px;
            background: #fcfdfe;
        }

        :deep(.upload-input-zone .section) {
            padding: 1.75rem 1.5rem;
        }
    }

    .fs-add__meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        margin-top: -0.5rem;
    }

    .fs-add__hint {
        margin: 0;
    }

    .fs-add__cancel {
        flex: 0 0 auto;
    }

    .fs-comments__label {
        display: block;
        margin-bottom: 0.4rem;
        font-size: 0.85rem;
        font-weight: 600;
        color: #475569;
    }

    // Quiet add affordance — a full-width text button with a hairline above it,
    // matching the reference.
    .fs-add-affordance {
        margin-top: 0.25rem;
        border-top: 1px solid #f1f5f9;
        border-radius: 0;
        padding-top: 0.875rem;
        padding-bottom: 0.875rem;
        color: #64748b;
        font-size: 0.85rem;

        :deep(.p-button-icon) {
            font-size: 0.85rem;
        }
    }
}
</style>
